import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Grid,
  useTheme,
  alpha,
  Chip,
  IconButton,
  Avatar,
  Stack,
  Paper,
  Fade,
  Grow,
  Zoom,
} from "@mui/material";
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Email as EmailIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  LocalShipping as SupplierIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  History as HistoryIcon,
  AttachMoney as AttachMoneyIcon,
  Store as StoreIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { DataContext } from "../../context/DataContext";
import { useNavigate } from "react-router-dom";
import SupplierLedgerModal from "./SupplierLedgerModal";
import SupplierTransactionDetailsModal from "./SupplierTransactionDetailsModal";

const SupplierActionModal = ({
  isOpen,
  onClose,
  supplier,
  isDarkMode = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { supplierTransactions } = useContext(DataContext);

  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [ledgerModalOpen, setLedgerModalOpen] = useState(false);

  if (!isOpen || !supplier) return null;

  // Helper functions
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0';
    return `₹${Number(value).toLocaleString('en-IN')}`;
  };

  const displayValue = (value, fallback = '—') => {
    return value || fallback;
  };

  const getBalanceColor = (value) => {
    const num = Number(value);
    if (num > 0) return '#10B981';
    if (num < 0) return '#EF4444';
    return '#6B7280';
  };

  const getInitials = (name) => {
    if (!name) return 'S';
    return name.charAt(0).toUpperCase();
  };

  const handleViewTransactions = () => setTransactionModalOpen(true);
  const handleViewLedger = () => setLedgerModalOpen(true);

  // Data items with enhanced styling
  const supplierInfo = [
    { icon: <PersonIcon />, label: 'Supplier Name', value: supplier.name, color: theme.palette.primary.main },
    // { icon: <AccountBalanceWalletIcon />, label: 'Shortcut', value: supplier.supplierShortcut || '—', color: '#8b5cf6' },
    { icon: <EmailIcon />, label: 'Email', value: supplier.email || '—', color: '#3b82f6' },
    { icon: <PhoneIcon />, label: 'Contact', value: supplier.contact || '—', color: theme.palette.primary.main },
    { icon: <LocationOnIcon />, label: 'Address', value: supplier.address || '—', color: '#8B5CF6' },
    { icon: <BusinessIcon />, label: 'GST Number', value: supplier.gstNumber || '—', color: '#f59e0b' },
    ...(supplier.panNumber ? [{ icon: <CreditCardIcon />, label: 'PAN Number', value: supplier.panNumber, color: '#ef4444' }] : []),
  ];

  const openingBalances = [
    { label: 'Cash', value: formatCurrency(supplier.openingBalanceCash), color: '#10B981', icon: <AttachMoneyIcon sx={{ fontSize: 14 }} /> },
    { label: 'Bank', value: formatCurrency(supplier.openingBalanceBank), color: '#3B82F6', icon: <AccountBalanceIcon sx={{ fontSize: 14 }} /> },
    { label: 'Fine (gms)', value: displayValue(supplier.openingBalanceFine, '0'), color: '#F59E0B', icon: <InventoryIcon sx={{ fontSize: 14 }} /> },
  ];

  const currentBalances = [
    { label: 'Cash', value: formatCurrency(supplier.balanceValueCash), color: getBalanceColor(supplier.balanceValueCash), icon: <AttachMoneyIcon sx={{ fontSize: 14 }} /> },
    { label: 'Bank', value: formatCurrency(supplier.balanceValueBank), color: getBalanceColor(supplier.balanceValueBank), icon: <AccountBalanceIcon sx={{ fontSize: 14 }} /> },
    { label: 'Fine (gms)', value: displayValue(supplier.balanceFine, '0'), color: getBalanceColor(supplier.balanceFine), icon: <InventoryIcon sx={{ fontSize: 14 }} /> },
  ];

  const bankDetails = [
    { icon: <BusinessIcon />, label: 'Bank Name', value: supplier.bankName || '—', color: '#8b5cf6' },
    { icon: <PersonIcon />, label: 'Account Holder', value: supplier.accountHolder || '—', color: '#8b5cf6' },
    { icon: <CreditCardIcon />, label: 'Account Number', value: supplier.accountNumber || '—', color: '#8b5cf6' },
    { icon: <AccountBalanceIcon />, label: 'IFSC Code', value: supplier.ifscCode || '—', color: '#8b5cf6' },
    ...(supplier.branch ? [{ icon: <LocationOnIcon />, label: 'Branch', value: supplier.branch, color: '#8b5cf6' }] : []),
  ];

  const hasBankDetails = bankDetails.some(detail => detail.value !== '—');
  const hasCurrentBalances = currentBalances.some(b => b.value !== '₹0' && b.value !== '0');

  // Calculate total balance
  const totalOpening = (Number(supplier.openingBalanceCash) || 0) + (Number(supplier.openingBalanceBank) || 0);

  const fetchSupplierTransactions = async (supplierId) => {
    console.log("Fetching transactions for supplier ID:", supplierId);
    let filteredTransactions = supplierTransactions?.filter((transaction) => {
      return transaction.supplier === supplierId ||
        transaction.supplierId === supplierId;
    });
    console.log(filteredTransactions);
    return filteredTransactions;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Zoom}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          background: isDarkMode
            ? 'linear-gradient(145deg, rgba(26, 35, 50, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)'
            : 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
          boxShadow: isDarkMode
            ? '0 25px 60px rgba(0,0,0,0.8), 0 0 40px rgba(59,130,246,0.05)'
            : '0 25px 60px rgba(0,0,0,0.15), 0 0 40px rgba(59,130,246,0.08)',
          overflow: 'hidden',
          maxHeight: '92vh',
          border: isDarkMode
            ? '1px solid rgba(255,255,255,0.06)'
            : '1px solid rgba(255,255,255,0.8)',
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      {/* Header with Premium Gradient */}
      <Box sx={{
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(139,92,246,0.15) 100%)'
            : 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.08) 100%)',
          opacity: 0.6,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: isDarkMode
            ? 'linear-gradient(90deg, #3b82f6, #8b5cf6, #3b82f6)'
            : 'linear-gradient(90deg, #2563eb, #7c3aed, #2563eb)',
          backgroundSize: '200% 100%',
          animation: 'gradientMove 3s ease infinite',
        },
      }}>
        <Box sx={{
          p: 3,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.dark, 0.7)})`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  border: `2px solid ${alpha('#ffffff', 0.2)}`,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {getInitials(supplier.name)}
              </Avatar>
              <Box sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#10B981',
                border: `2px solid ${isDarkMode ? '#1a2332' : '#ffffff'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <StoreIcon sx={{ fontSize: 10, color: '#ffffff' }} />
              </Box>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{
                  fontWeight: 800,
                  fontSize: '1.25rem',
                  background: isDarkMode
                    ? 'linear-gradient(135deg, #f1f5f9 0%, #94a3b8 100%)'
                    : 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  {supplier.name}
                </Typography>
                {supplier.supplierShortcut && (
                  <Chip
                    label={supplier.supplierShortcut}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${alpha('#8b5cf6', 0.15)}, ${alpha('#7c3aed', 0.15)})`,
                      color: '#8b5cf6',
                      border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
                      backdropFilter: 'blur(10px)',
                      '& .MuiChip-label': {
                        px: 1.5,
                      },
                    }}
                  />
                )}
              </Box>
              <Typography sx={{
                fontSize: '0.75rem',
                color: theme.palette.text.secondary,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.25,
              }}>
                <SupplierIcon sx={{ fontSize: 13, color: theme.palette.primary.main }} />
                Supplier • {supplier.gstNumber ? `GST: ${supplier.gstNumber}` : 'No GST'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              background: isDarkMode
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.04)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                background: isDarkMode
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgba(0,0,0,0.08)',
                color: theme.palette.primary.main,
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3, pt: 2.5 }}>
        <Grid container spacing={2.5}>
          {/* Supplier Information - Always Visible */}
          <Grid item xs={12}>
            <Fade in={true} timeout={500}>
              <Paper
                elevation={0}
                sx={{
                  minWidth: 840,
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                  background: isDarkMode
                    ? 'rgba(255,255,255,0.02)'
                    : 'rgba(255,255,255,0.6)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box sx={{
                  p: 1.5,
                  px: 2,
                  background: isDarkMode
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.15)}, ${alpha(theme.palette.secondary.dark, 0.15)})`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.08)}, ${alpha(theme.palette.secondary.light, 0.08)})`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <BusinessIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      color: theme.palette.text.primary,
                      letterSpacing: '0.3px',
                    }}>
                      Supplier Information
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={1.5}>
                    {supplierInfo.map((item, index) => (
                      <Grid item xs={6} sm={4} md={3} key={index}>
                        <Zoom in={true} timeout={300 + index * 50}>
                          <Box sx={{
                            p: 1.25,
                            borderRadius: '12px',
                            background: isDarkMode
                              ? `linear-gradient(135deg, ${alpha(item.color, 0.06)}, rgba(255,255,255,0.02))`
                              : `linear-gradient(135deg, ${alpha(item.color, 0.04)}, rgba(255,255,255,0.8))`,
                            border: `1px solid ${alpha(item.color, 0.08)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${alpha(item.color, 0.1)}`,
                              borderColor: alpha(item.color, 0.2),
                            },
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{
                                width: 28,
                                height: 28,
                                minWidth: 28,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${alpha(item.color, 0.15)}, ${alpha(item.color, 0.05)})`,
                                border: `1px solid ${alpha(item.color, 0.1)}`,
                              }}>
                                {React.cloneElement(item.icon, {
                                  sx: { fontSize: 14, color: item.color }
                                })}
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{
                                  fontSize: '0.55rem',
                                  color: theme.palette.text.secondary,
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                  display: 'block',
                                }}>
                                  {item.label}
                                </Typography>
                                <Typography sx={{
                                  fontSize: '0.8rem',
                                  fontWeight: 700,
                                  color: theme.palette.text.primary,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {item.value}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Zoom>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            </Fade>
          </Grid>


          {/* Premium Action Cards - Always Visible */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Grow in={true} timeout={300}>
                  <Paper
                    elevation={0}
                    sx={{
                      minWidth: 250,
                      p: 2,
                      borderRadius: '16px',
                      background: isDarkMode
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.2)}, ${alpha(theme.palette.primary.dark, 0.05)})`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)}, ${alpha(theme.palette.primary.light, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      transition: 'all 0.3s ease',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.primary.dark, 0.2)})`,
                        color: theme.palette.primary.main,
                      }}>
                        <ReceiptIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography sx={{
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        color: theme.palette.text.primary,
                        flex: 1,
                      }}>
                        Purchase History
                      </Typography>
                    </Box>
                    <Stack spacing={1}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
                        onClick={handleViewTransactions}
                        sx={{
                          py: 0.8,
                          borderRadius: '10px',
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                          color: theme.palette.primary.main,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          justifyContent: 'center',
                          background: isDarkMode
                            ? 'rgba(255,255,255,0.02)'
                            : 'rgba(255,255,255,0.5)',
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            background: alpha(theme.palette.primary.main, 0.05),
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        View All Purchases
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                        onClick={() => navigate('/supplier-transaction')}
                        sx={{
                          py: 0.8,
                          borderRadius: '10px',
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                          '&:hover': {
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.dark})`,
                            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Add New Purchase
                      </Button>
                    </Stack>
                  </Paper>
                </Grow>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Grow in={true} timeout={400}>
                  <Paper
                    elevation={0}
                    sx={{
                      minWidth: 250,
                      p: 2,
                      borderRadius: '16px',
                      background: isDarkMode
                        ? `linear-gradient(135deg, ${alpha('#10B981', 0.15)}, ${alpha('#10B981', 0.05)})`
                        : `linear-gradient(135deg, ${alpha('#10B981', 0.12)}, ${alpha('#10B981', 0.03)})`,
                      border: `1px solid ${alpha('#10B981', 0.15)}`,
                      transition: 'all 0.3s ease',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${alpha('#10B981', 0.15)}`,
                        borderColor: alpha('#10B981', 0.3),
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha('#10B981', 0.2)}, ${alpha('#10B981', 0.2)})`,
                        color: '#10B981',
                      }}>
                        <AccountBalanceIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography sx={{
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        color: theme.palette.text.primary,
                        flex: 1,
                      }}>
                        Ledger Management
                      </Typography>
                    </Box>
                    <Stack spacing={1}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
                        onClick={handleViewLedger}
                        sx={{
                          py: 0.8,
                          borderRadius: '10px',
                          borderColor: alpha('#10B981', 0.2),
                          color: '#10B981',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          background: isDarkMode
                            ? 'rgba(255,255,255,0.02)'
                            : 'rgba(255,255,255,0.5)',
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            borderColor: '#10B981',
                            background: alpha('#10B981', 0.05),
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        View Ledger Balance
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                        onClick={() => navigate('/supplier-ledger')}
                        sx={{
                          py: 0.8,
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #10B981, #059669)',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          boxShadow: `0 4px 15px ${alpha('#10B981', 0.3)}`,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #059669, #047857)',
                            boxShadow: `0 6px 20px ${alpha('#10B981', 0.4)}`,
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Add Ledger Entry
                      </Button>
                    </Stack>
                  </Paper>
                </Grow>
              </Grid>
            </Grid>
          </Grid>



          {/* Balances Section - Always Visible with Placeholder Data */}
          <Grid item xs={12}>
            <Grid container spacing={1.5}>
              {/* Opening Balances - Always Visible */}
              <Grid item xs={12} md={hasCurrentBalances ? 6 : 12}>
                <Fade in={true} timeout={600}>
                  <Paper
                    elevation={0}
                    sx={{
                      minWidth: 300,
                      p: 2,
                      borderRadius: '16px',
                      border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                      background: isDarkMode
                        ? 'rgba(255,255,255,0.02)'
                        : 'rgba(255,255,255,0.6)',
                      backdropFilter: 'blur(10px)',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 8px 25px ${alpha('#f59e0b', 0.08)}`,
                        borderColor: alpha('#f59e0b', 0.15),
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha('#f59e0b', 0.2)}, ${alpha('#f59e0b', 0.05)})`,
                        color: '#f59e0b',
                      }}>
                        <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Typography sx={{
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        color: theme.palette.text.primary,
                        flex: 1,
                      }}>
                        Opening Balances
                      </Typography>
                      <Typography sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: '#f59e0b',
                        background: alpha('#f59e0b', 0.1),
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                      }}>
                        {formatCurrency(totalOpening)}
                      </Typography>
                    </Box>
                    <Grid container spacing={1.5}>
                      {openingBalances.map((balance, index) => (
                        <Grid item xs={4} key={index}>
                          <Zoom in={true} timeout={300 + index * 100}>
                            <Box sx={{
                              minWidth: 80,
                              p: 1.5,
                              borderRadius: '12px',
                              textAlign: 'center',
                              background: `linear-gradient(135deg, ${alpha(balance.color, 0.08)}, ${alpha(balance.color, 0.02)})`,
                              border: `1px solid ${alpha(balance.color, 0.12)}`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${alpha(balance.color, 0.1)}`,
                                borderColor: alpha(balance.color, 0.25),
                              },
                            }}>
                              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                                <Box sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: alpha(balance.color, 0.1),
                                  color: balance.color,
                                }}>
                                  {balance.icon}
                                </Box>
                              </Box>
                              <Typography sx={{
                                fontSize: '0.55rem',
                                color: theme.palette.text.secondary,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                mb: 0.25,
                              }}>
                                {balance.label}
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: balance.color,
                              }}>
                                {balance.value}
                              </Typography>
                            </Box>
                          </Zoom>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Fade>
              </Grid>


            </Grid>
          </Grid>

          {/* Bank Details - Always Visible with Placeholder if no data */}
          <Grid item xs={12}>
            <Fade in={true} timeout={800}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                  background: isDarkMode
                    ? 'rgba(255,255,255,0.02)'
                    : 'rgba(255,255,255,0.6)',
                  backdropFilter: 'blur(10px)',
                  minWidth: 850,
                }}
              >
                <Box sx={{
                  p: 1.5,
                  px: 2,
                  background: isDarkMode
                    ? `linear-gradient(135deg, ${alpha('#8b5cf6', 0.12)}, ${alpha('#7c3aed', 0.12)})`
                    : `linear-gradient(135deg, ${alpha('#8b5cf6', 0.06)}, ${alpha('#7c3aed', 0.06)})`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <AccountBalanceIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      color: theme.palette.text.primary,
                      letterSpacing: '0.3px',
                    }}>
                      Bank Details
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={1}>
                    {bankDetails.map((detail, index) => (
                      <Grid item xs={6} sm={4} md={2.4} key={index}>
                        <Zoom in={true} timeout={300 + index * 50}>
                          <Box sx={{
                            minWidth: 150,
                            p: 1.25,
                            borderRadius: '12px',
                            background: isDarkMode
                              ? `linear-gradient(135deg, ${alpha(detail.color, 0.06)}, rgba(255,255,255,0.02))`
                              : `linear-gradient(135deg, ${alpha(detail.color, 0.04)}, rgba(255,255,255,0.8))`,
                            border: `1px solid ${alpha(detail.color, 0.08)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${alpha(detail.color, 0.1)}`,
                              borderColor: alpha(detail.color, 0.2),
                            },
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{
                                width: 28,
                                height: 28,
                                minWidth: 28,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${alpha(detail.color, 0.15)}, ${alpha(detail.color, 0.05)})`,
                                border: `1px solid ${alpha(detail.color, 0.1)}`,
                              }}>
                                {React.cloneElement(detail.icon, {
                                  sx: { fontSize: 14, color: detail.color }
                                })}
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{
                                  fontSize: '0.55rem',
                                  color: theme.palette.text.secondary,
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                  display: 'block',
                                }}>
                                  {detail.label}
                                </Typography>
                                <Typography sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  color: theme.palette.text.primary,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {detail.value}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Zoom>
                      </Grid>
                    ))}
                  </Grid>
                  {!hasBankDetails && (
                    <Box sx={{
                      mt: 1,
                      p: 1.5,
                      textAlign: 'center',
                      borderRadius: '10px',
                      background: alpha(theme.palette.text.secondary, 0.05),
                    }}>
                      <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary, mr: 1, verticalAlign: 'middle' }} />
                      <Typography sx={{
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                        display: 'inline',
                      }}>
                        No bank details available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      </DialogContent>

      <SupplierTransactionDetailsModal
        isOpen={transactionModalOpen}
        onClose={() => setTransactionModalOpen(false)}
        supplier={supplier}
        fetchSupplierTransactions={fetchSupplierTransactions}
      />

      {/* Supplier Ledger Modal */}
      <SupplierLedgerModal
        isOpen={ledgerModalOpen}
        onClose={() => setLedgerModalOpen(false)}
        supplier={supplier}
        fetchSupplierTransactions={fetchSupplierTransactions}
        isDarkMode={isDarkMode}
      />

      {/* CSS Animation Keyframes */}
      <style>
        {`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
    </Dialog>
  );
};

export default SupplierActionModal;